import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSCGGraphData } from '../services/api';
import {
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowRightIcon,
  ArrowLongRightIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';

// ── Classification palette ──────────────────────────────────────────
const classColors = {
  'UNCLASSIFIED':  { hex: '#22c55e', bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.35)' },
  'CONFIDENTIAL':  { hex: '#3b82f6', bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.35)' },
  'SECRET':        { hex: '#ef4444', bg: 'rgba(239,68,68,0.12)',   border: 'rgba(239,68,68,0.35)' },
  'TOP SECRET':    { hex: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: 'rgba(251,191,36,0.35)' },
};

const classHierarchy = { 'UNCLASSIFIED': 0, 'CONFIDENTIAL': 1, 'SECRET': 2, 'TOP SECRET': 3 };

function classAbbrev(c) {
  if (!c) return '?';
  const map = { 'UNCLASSIFIED': 'U', 'CONFIDENTIAL': 'C', 'SECRET': 'S', 'TOP SECRET': 'TS' };
  return map[c] || c;
}

// ── Reusable tiny components ────────────────────────────────────────

function ClassBadge({ classification, small = false }) {
  const colors = classColors[classification] || classColors['UNCLASSIFIED'];
  return (
    <span style={{
      display: 'inline-block',
      borderRadius: '4px',
      fontWeight: 700,
      color: '#fff',
      background: colors.hex,
      padding: small ? '1px 6px' : '2px 8px',
      fontSize: small ? '9px' : '10px',
      letterSpacing: '0.3px',
      lineHeight: '16px',
      whiteSpace: 'nowrap',
    }}>
      {classification}
    </span>
  );
}

function AccordionSection({ title, subtitle, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: '#1e293b',
      border: '1px solid #334155',
      borderRadius: '10px',
      overflow: 'hidden',
      marginBottom: '12px',
    }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '14px 18px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          color: '#e2e8f0',
          textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
          {open
            ? <ChevronDownIcon style={{ width: 16, height: 16, color: '#f59e0b', flexShrink: 0 }} />
            : <ChevronRightIcon style={{ width: 16, height: 16, color: '#94a3b8', flexShrink: 0 }} />
          }
          <span style={{ fontWeight: 600, fontSize: '14px' }}>{title}</span>
          {badge && (
            <span style={{
              background: 'rgba(245,158,11,0.15)',
              border: '1px solid rgba(245,158,11,0.3)',
              color: '#fbbf24',
              fontSize: '10px',
              fontWeight: 700,
              padding: '1px 8px',
              borderRadius: '10px',
            }}>
              {badge}
            </span>
          )}
        </div>
        {subtitle && (
          <span style={{ fontSize: '12px', color: '#64748b', flexShrink: 0, marginLeft: 12 }}>
            {subtitle}
          </span>
        )}
      </button>
      {open && (
        <div style={{ padding: '0 18px 16px 18px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

function KeywordTag({ text }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '1px 7px',
      fontSize: '10px',
      fontWeight: 500,
      borderRadius: '8px',
      background: '#334155',
      color: '#94a3b8',
      border: '1px solid #475569',
      marginRight: '4px',
      marginBottom: '4px',
    }}>
      {text}
    </span>
  );
}

// ── Rule card (used in Triggered Rules and expandable path nodes) ───
function RuleCard({ node, compact = false }) {
  const [expanded, setExpanded] = useState(false);
  const colors = classColors[node.data?.classification] || classColors['UNCLASSIFIED'];
  const isTriggered = node.data?.type === 'triggered';

  if (compact) {
    return (
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          background: '#0f172a',
          border: `1px solid ${colors.border}`,
          borderLeft: `3px solid ${colors.hex}`,
          borderRadius: '8px',
          padding: '10px 14px',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontFamily: 'monospace', fontWeight: 700, fontSize: '12px', color: '#e2e8f0',
            }}>
              {node.data?.label || node.id}
            </span>
            <ClassBadge classification={node.data?.classification} small />
          </div>
          {expanded
            ? <ChevronDownIcon style={{ width: 14, height: 14, color: '#64748b' }} />
            : <ChevronRightIcon style={{ width: 14, height: 14, color: '#64748b' }} />
          }
        </div>
        {expanded && (
          <div style={{ marginTop: '10px' }}>
            {node.data?.item && (
              <p style={{ color: '#94a3b8', fontSize: '12px', lineHeight: 1.5, margin: '0 0 8px 0' }}>
                {node.data.item}
              </p>
            )}
            {node.data?.reason && (
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Reason
                </span>
                <p style={{ color: '#cbd5e1', fontSize: '11px', margin: '2px 0 0 0', lineHeight: 1.4 }}>
                  {node.data.reason}
                </p>
              </div>
            )}
            {node.data?.keywords?.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {node.data.keywords.map((kw, i) => <KeywordTag key={i} text={kw} />)}
              </div>
            )}
            {node.data?.caveats?.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {node.data.caveats.map((c, i) => (
                  <span key={i} style={{
                    display: 'inline-block', padding: '1px 7px', fontSize: '10px', fontWeight: 600,
                    borderRadius: '8px', background: 'rgba(245,158,11,0.1)', color: '#fcd34d',
                    border: '1px solid rgba(245,158,11,0.25)',
                  }}>
                    {c}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full card (for triggered rules section)
  return (
    <div style={{
      background: '#0f172a',
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${colors.hex}`,
      borderRadius: '8px',
      padding: '14px 16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px',
            color: isTriggered ? '#f87171' : '#fb923c',
          }}>
            {isTriggered ? 'TRIGGERED' : 'HOPPED'}
          </span>
          <ClassBadge classification={node.data?.classification} small />
        </div>
        <span style={{
          fontFamily: 'monospace', fontWeight: 700, fontSize: '12px', color: '#e2e8f0',
        }}>
          {node.data?.label || node.id}
        </span>
      </div>

      {node.data?.item && (
        <p style={{
          color: '#94a3b8', fontSize: '12px', lineHeight: 1.5, margin: '0 0 8px 0',
          overflow: 'hidden', textOverflow: 'ellipsis',
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        }}>
          {node.data.item}
        </p>
      )}

      {node.data?.keywords?.length > 0 && (
        <div>
          {node.data.keywords.map((kw, i) => <KeywordTag key={i} text={kw} />)}
        </div>
      )}
    </div>
  );
}

// ── Hop path breadcrumb trail ───────────────────────────────────────
function HopPathTrail({ path, nodeMap }) {
  const [expanded, setExpanded] = useState(false);

  // Build the ordered chain: source -> via[] -> target
  const chain = [path.source, ...(path.via || []), path.target];
  const avgSim = path.distance != null ? ((1 - path.distance) * 100).toFixed(0) : null;

  return (
    <div style={{
      background: '#0f172a',
      border: '1px solid #334155',
      borderRadius: '10px',
      padding: '14px 16px',
      marginBottom: '10px',
    }}>
      {/* Breadcrumb */}
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '6px',
          cursor: 'pointer',
        }}
      >
        {chain.map((ruleId, idx) => {
          const node = nodeMap[ruleId];
          const classification = node?.data?.classification || 'UNCLASSIFIED';
          const colors = classColors[classification] || classColors['UNCLASSIFIED'];
          const isTarget = idx === chain.length - 1;

          return (
            <React.Fragment key={ruleId}>
              {idx > 0 && (
                <ArrowLongRightIcon style={{
                  width: 20, height: 20,
                  color: '#475569',
                  flexShrink: 0,
                }} />
              )}
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                padding: '4px 10px',
                borderRadius: '6px',
                background: isTarget ? colors.bg : '#1e293b',
                border: `1px solid ${isTarget ? colors.hex : '#334155'}`,
                boxShadow: isTarget ? `0 0 8px ${colors.border}` : 'none',
                transition: 'background 0.15s',
              }}>
                <span style={{
                  fontFamily: 'monospace', fontSize: '11px', fontWeight: 700, color: '#e2e8f0',
                }}>
                  {ruleId}
                </span>
                <span style={{
                  display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                  background: colors.hex, flexShrink: 0,
                }} />
                <span style={{ fontSize: '10px', fontWeight: 700, color: colors.hex }}>
                  {classAbbrev(classification)}
                </span>
              </span>
            </React.Fragment>
          );
        })}

        <span style={{ marginLeft: 'auto', flexShrink: 0 }}>
          {expanded
            ? <ChevronDownIcon style={{ width: 14, height: 14, color: '#64748b' }} />
            : <ChevronRightIcon style={{ width: 14, height: 14, color: '#64748b' }} />
          }
        </span>
      </div>

      {/* Summary line */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '12px',
        marginTop: '8px', fontSize: '11px', color: '#64748b',
      }}>
        <span>{path.hops} hop{path.hops !== 1 ? 's' : ''}</span>
        {avgSim && (
          <>
            <span style={{ color: '#475569' }}>&middot;</span>
            <span>{avgSim}% similarity</span>
          </>
        )}
        {path.reasoning && (
          <>
            <span style={{ color: '#475569' }}>&middot;</span>
            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
              {path.reasoning.length > 80 ? path.reasoning.slice(0, 80) + '...' : path.reasoning}
            </span>
          </>
        )}
      </div>

      {/* Expanded: show each node in the path */}
      {expanded && (
        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {chain.map(ruleId => {
            const node = nodeMap[ruleId];
            if (!node) {
              return (
                <div key={ruleId} style={{
                  padding: '8px 12px', background: '#1e293b', borderRadius: '6px',
                  border: '1px solid #334155', fontFamily: 'monospace', fontSize: '12px', color: '#94a3b8',
                }}>
                  {ruleId} (details not available)
                </div>
              );
            }
            return <RuleCard key={ruleId} node={node} compact />;
          })}

          {path.reasoning && (
            <div style={{
              padding: '10px 12px',
              background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: '6px',
              fontSize: '11px',
              color: '#a5b4fc',
              lineHeight: 1.5,
            }}>
              <span style={{ fontWeight: 700, fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#818cf8' }}>
                Reasoning
              </span>
              <p style={{ margin: '4px 0 0 0' }}>{path.reasoning}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Iteration accordion item ────────────────────────────────────────
function IterationItem({ iteration, idx }) {
  const [expanded, setExpanded] = useState(false);
  const iterNum = iteration.segment_iteration || iteration.iteration_number || idx + 1;
  const strategy = iteration.strategy_used || iteration.strategy;
  const afterClass = iteration.classification_after || iteration.result_classification?.classification || iteration.classification_result?.classification;
  const beforeClass = iteration.classification_before || iteration.original_classification?.classification;
  const madeProgress = (classHierarchy[afterClass] ?? 3) < (classHierarchy[beforeClass] ?? 3);
  const scgRulesUsed = iteration.scg_rules_used || [];
  const triggeredCount = scgRulesUsed.filter(r => (r.hop_distance || 0) === 0).length;
  const hoppedCount = scgRulesUsed.filter(r => (r.hop_distance || 0) > 0).length;

  const borderColor = madeProgress ? 'rgba(34,197,94,0.4)' : '#334155';
  const bgColor = madeProgress ? 'rgba(34,197,94,0.04)' : '#0f172a';

  return (
    <div style={{
      background: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: '8px',
      overflow: 'hidden',
      marginBottom: '8px',
    }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 14px', background: 'transparent', border: 'none', cursor: 'pointer',
          color: '#e2e8f0', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: '12px', color: '#fff',
            background: madeProgress ? '#16a34a' : '#475569',
          }}>
            {iterNum}
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0' }}>
              Iteration {iterNum}
              {iteration.segment_id !== undefined && (
                <span style={{ color: '#64748b', marginLeft: 4 }}>(Seg {iteration.segment_id})</span>
              )}
            </div>
            <div style={{ fontSize: '11px', color: '#64748b' }}>
              {strategy && <span style={{ textTransform: 'capitalize' }}>{strategy}</span>}
              {triggeredCount > 0 && <span> &middot; {triggeredCount} triggered</span>}
              {hoppedCount > 0 && <span> &middot; {hoppedCount} hopped</span>}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {beforeClass && <ClassBadge classification={beforeClass} small />}
          <ArrowRightIcon style={{ width: 14, height: 14, color: madeProgress ? '#22c55e' : '#475569' }} />
          {afterClass && <ClassBadge classification={afterClass} small />}
          {expanded
            ? <ChevronDownIcon style={{ width: 14, height: 14, color: '#64748b', marginLeft: 4 }} />
            : <ChevronRightIcon style={{ width: 14, height: 14, color: '#64748b', marginLeft: 4 }} />
          }
        </div>
      </button>

      {expanded && scgRulesUsed.length > 0 && (
        <div style={{ padding: '0 14px 12px 14px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: 8 }}>
            {scgRulesUsed.map((r, i) => {
              const rId = r.rule_id || r.id;
              const isHopped = (r.hop_distance || 0) > 0;
              return (
                <span key={i} style={{
                  display: 'inline-block', padding: '2px 8px', borderRadius: '4px',
                  fontSize: '11px', fontWeight: 600, fontFamily: 'monospace',
                  background: isHopped ? 'rgba(249,115,22,0.12)' : 'rgba(239,68,68,0.12)',
                  color: isHopped ? '#fb923c' : '#f87171',
                  border: `1px solid ${isHopped ? 'rgba(249,115,22,0.3)' : 'rgba(239,68,68,0.3)'}`,
                }}>
                  {rId}{isHopped && ` (${r.hop_distance}h)`}
                </span>
              );
            })}
          </div>

          {scgRulesUsed.filter(r => r.sanitization_guidance).length > 0 && (
            <div style={{
              padding: '8px 10px', borderRadius: '6px', fontSize: '11px', lineHeight: 1.5,
              background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)', color: '#86efac',
            }}>
              {scgRulesUsed.filter(r => r.sanitization_guidance).map((r, i) => (
                <div key={i}>{r.sanitization_guidance}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════════════

export default function SCGRuleHoppingGraph({
  isOpen,
  onClose,
  tearlineData,
  segmentIndex: _segmentIndex = null,
}) {
  // ── Derive triggered rule IDs ─────────────────────────────────
  const triggeredRuleIds = useMemo(() => {
    const ids = new Set();
    if (tearlineData?.original_classification?.scg_rules_triggered) {
      tearlineData.original_classification.scg_rules_triggered.forEach(id => ids.add(id));
    }
    tearlineData?.iterations?.forEach(iter => {
      (iter.scg_rules_triggered || []).forEach(id => ids.add(id));
      (iter.scg_rules_used || []).forEach(r => ids.add(r.rule_id || r.id));
    });
    return Array.from(ids);
  }, [tearlineData]);

  // ── Fetch graph data ──────────────────────────────────────────
  const { data: graphData, isLoading } = useQuery({
    queryKey: ['scg-graph', triggeredRuleIds.join(','), tearlineData?.target_classification],
    queryFn: () => getSCGGraphData(
      triggeredRuleIds,
      tearlineData?.target_classification || 'UNCLASSIFIED',
      tearlineData?.target_caveats || [],
      tearlineData?.target_releasability || [],
    ),
    enabled: triggeredRuleIds.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  // ── Derived data ──────────────────────────────────────────────
  const nodeMap = useMemo(() => {
    const map = {};
    (graphData?.nodes || []).forEach(n => { map[n.id] = n; });
    return map;
  }, [graphData]);

  const triggeredNodes = useMemo(
    () => (graphData?.nodes || []).filter(n => n.data?.type === 'triggered'),
    [graphData],
  );
  const hoppedNodes = useMemo(
    () => (graphData?.nodes || []).filter(n => n.data?.type === 'hopped'),
    [graphData],
  );
  const hopPaths = graphData?.hop_paths || [];

  const iterations = tearlineData?.iterations || [];

  // Header info
  const original = tearlineData?.original_classification?.full_classification
    || tearlineData?.original_classification?.classification || 'Unknown';
  const target = tearlineData?.target_classification || 'UNCLASSIFIED';
  const final = tearlineData?.final_classification?.full_classification
    || tearlineData?.final_classification?.classification || 'Unknown';
  const success = tearlineData?.success;

  // Early return AFTER all hooks
  if (!isOpen) return null;

  // ── Render ────────────────────────────────────────────────────
  return (
    <div style={{
      position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 50, padding: '16px',
      background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
    }}>
      <div style={{
        borderRadius: '12px', width: '100%', maxWidth: '900px', maxHeight: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        border: '1px solid #334155', background: '#111827',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.6)',
      }}>
        {/* ── Header ─────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 24px',
          borderBottom: '1px solid #334155',
          background: 'linear-gradient(135deg, #312e81 0%, #1e1b4b 50%, #0f172a 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(99,102,241,0.2)', border: '1px solid rgba(99,102,241,0.3)',
            }}>
              <ShieldCheckIcon style={{ width: 24, height: 24, color: '#a5b4fc' }} />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#fff' }}>
                SCG Rule Analysis
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 2 }}>
                <span style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '12px' }}>{original}</span>
                <ArrowRightIcon style={{ width: 14, height: 14, color: '#818cf8' }} />
                <span style={{ color: '#cbd5e1', fontFamily: 'monospace', fontSize: '12px' }}>{target}</span>
                <span style={{
                  marginLeft: 8, padding: '1px 10px', borderRadius: '10px',
                  fontSize: '10px', fontWeight: 700,
                  background: success ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)',
                  color: success ? '#86efac' : '#fcd34d',
                  border: `1px solid ${success ? 'rgba(34,197,94,0.3)' : 'rgba(245,158,11,0.3)'}`,
                }}>
                  {success ? 'TARGET ACHIEVED' : 'PARTIAL'} &rarr; {final}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: 8, borderRadius: 8, border: 'none', background: 'transparent',
              cursor: 'pointer', color: '#94a3b8', transition: 'color 0.15s',
            }}
            onMouseOver={e => e.currentTarget.style.color = '#fff'}
            onMouseOut={e => e.currentTarget.style.color = '#94a3b8'}
          >
            <XMarkIcon style={{ width: 20, height: 20 }} />
          </button>
        </div>

        {/* ── Summary bar ────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '20px', padding: '12px 24px',
          borderBottom: '1px solid #1e293b', background: '#0f172a',
          flexWrap: 'wrap',
        }}>
          <SummaryStat
            label="Triggered"
            value={triggeredNodes.length}
            color="#f87171"
          />
          <SummaryStat
            label="Discovered"
            value={hoppedNodes.length}
            color="#fb923c"
          />
          <SummaryStat
            label="Hop Paths"
            value={hopPaths.length}
            color="#818cf8"
          />
          <SummaryStat
            label="Iterations"
            value={iterations.length}
            color="#22d3ee"
          />
          {isLoading && (
            <span style={{ fontSize: '11px', color: '#64748b', fontStyle: 'italic' }}>
              Loading graph data...
            </span>
          )}
        </div>

        {/* ── Scrollable content ─────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px', background: '#111827' }}>

          {triggeredRuleIds.length === 0 && !isLoading ? (
            <div style={{ textAlign: 'center', padding: '48px 0' }}>
              <DocumentTextIcon style={{ width: 48, height: 48, color: '#334155', margin: '0 auto 12px' }} />
              <p style={{ color: '#64748b', fontSize: '14px' }}>No triggered rules to display</p>
            </div>
          ) : (
            <>
              {/* ── 1. Triggered Rules ────────────────────────── */}
              <AccordionSection
                title="Triggered Rules"
                badge={triggeredNodes.length > 0 ? String(triggeredNodes.length) : undefined}
                subtitle="Rules activated by document content"
                defaultOpen={hopPaths.length === 0}
              >
                {triggeredNodes.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {triggeredNodes.map(node => (
                      <RuleCard key={node.id} node={node} />
                    ))}
                  </div>
                ) : (
                  <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>
                    {isLoading ? 'Loading...' : 'No triggered rules found'}
                  </p>
                )}
              </AccordionSection>

              {/* ── 2. Rule Hopping Paths (main focus) ────────── */}
              <AccordionSection
                title="Rule Hopping Paths"
                badge={hopPaths.length > 0 ? String(hopPaths.length) : undefined}
                subtitle="Paths to lower-classification rules"
                defaultOpen={hopPaths.length > 0}
              >
                {hopPaths.length > 0 ? (
                  hopPaths.map((path, i) => (
                    <HopPathTrail key={i} path={path} nodeMap={nodeMap} />
                  ))
                ) : (
                  <div style={{
                    textAlign: 'center', padding: '24px 0', color: '#64748b', fontSize: '13px',
                  }}>
                    {isLoading
                      ? 'Searching for hop paths...'
                      : 'No hop paths discovered. All rules are at the same level or unconnected.'}
                  </div>
                )}
              </AccordionSection>

              {/* ── 3. Iteration Timeline ─────────────────────── */}
              <AccordionSection
                title="Iteration Timeline"
                badge={iterations.length > 0 ? String(iterations.length) : undefined}
                subtitle="Step-by-step sanitization progress"
                defaultOpen={false}
              >
                {iterations.length > 0 ? (
                  iterations.map((iter, idx) => (
                    <IterationItem key={`iter-${iter.segment_id || 'main'}-${idx}`} iteration={iter} idx={idx} />
                  ))
                ) : (
                  <div style={{ textAlign: 'center', padding: '24px 0' }}>
                    <DocumentTextIcon style={{ width: 36, height: 36, color: '#334155', margin: '0 auto 8px' }} />
                    <p style={{ color: '#64748b', fontSize: '13px', margin: 0 }}>No iteration data available</p>
                  </div>
                )}
              </AccordionSection>
            </>
          )}

          {/* ── Legend ────────────────────────────────────────── */}
          <div style={{
            padding: '10px 14px', borderRadius: '8px', marginTop: '4px',
            background: '#0f172a', border: '1px solid #1e293b',
            display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px',
            fontSize: '11px', color: '#64748b',
          }}>
            <span style={{ fontWeight: 600, color: '#475569' }}>Legend:</span>
            {Object.entries(classColors).map(([level, c]) => (
              <div key={level} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.hex, display: 'inline-block' }} />
                <span>{level}</span>
              </div>
            ))}
            <span style={{ color: '#334155' }}>|</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: 10, height: 10, borderRadius: '2px', border: '2px solid #f87171', display: 'inline-block' }} />
              <span>Triggered</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <span style={{ width: 10, height: 10, borderRadius: '2px', border: '2px solid #fb923c', display: 'inline-block' }} />
              <span>Hopped</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Small stat pill for the summary bar
function SummaryStat({ label, value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', background: color,
        boxShadow: `0 0 6px ${color}40`,
        display: 'inline-block',
      }} />
      <span style={{ fontSize: '13px', fontWeight: 700, color: '#e2e8f0' }}>{value}</span>
      <span style={{ fontSize: '11px', color: '#64748b' }}>{label}</span>
    </div>
  );
}
